import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAsyncState } from '@vueuse/core';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { normalizeInteger, normalizeLowercaseString, normalizeString, normalizeStringArray, normalizeStringOrNull } from '@dashboardApp/helpers/normalize';
import { jsonapiPreservedStore, sortToPreservedJson } from '@dashboardApp/store';
import router from '@dashboardApp/router';

const DEFAULT_LIMIT = 50;
const DEFAULT_SORT_KEY = 'growth';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_REPORTING_PERIOD = '12mo';
const PENDING_TRACKED_IDEAS_POLL_INTERVAL_MS = 30000;
const TRACKED_PRODUCT_IDEAS_ROUTE = 'research-tracked-product-ideas';

function createDefaultFilters() {
    return {
        query: '',
        category_slug: '',
        market_opportunity: [],
        demand_level: [],
        competition_level: [],
        interest_trend_window: DEFAULT_REPORTING_PERIOD,
    };
}

function createDefaultPagination() {
    return {
        current_page: 1,
        per_page: DEFAULT_LIMIT,
        total: 0,
        total_pages: 1,
        count: 0,
    };
}

export const useTrackedProductIdeasStore = defineStore('TrackedProductIdeasStore', () => {
    const route = useRoute();
    const preservedStore = jsonapiPreservedStore();
    const trackedIdeas = ref([]);
    const reportProducts = ref([]);
    const reportProductsLoaded = ref(false);
    let reportProductsRequest = null;
    // Pending rows stay here until resolved, and resolved rows remain buffered until the next route refresh.
    const pendingTrackedIdeas = ref([]);
    const filters = reactive(createDefaultFilters());
    const pagination = reactive(createDefaultPagination());
    const sortKey = ref(DEFAULT_SORT_KEY);
    const sortOrder = ref(DEFAULT_SORT_ORDER);
    let pendingTrackedIdeasPoller = null;
    let pendingTrackedIdeasPollToken = 0;
    let pendingTrackedIdeasPollInFlight = false;
    const totalPages = computed(() => Math.max(1, Number(pagination.total_pages || 1)));
    const persistedPendingTrackedIdeas = computed(() => trackedIdeas.value.filter((record) => isPendingTrackedIdeaRecord(record)));
    const readyTrackedIdeas = computed(() => trackedIdeas.value.filter((record) => !isPendingTrackedIdeaRecord(record)));
    const visiblePendingTrackedIdeas = computed(() => {
        if (pagination.current_page !== 1) {
            return [];
        }

        if (
            filters.category_slug
            || filters.market_opportunity.length
            || filters.demand_level.length
            || filters.competition_level.length
        ) {
            return [];
        }

        const normalizedQuery = normalizeLowercaseString(filters.query);
        const completedIdeaKeys = new Set(readyTrackedIdeas.value.map((record) => buildIdeaMatchKey(record)).filter(Boolean));

        return pendingTrackedIdeas.value.filter((record) => {
            const key = buildIdeaMatchKey(record);
            const term = normalizeLowercaseString(record?.term ?? record?.title ?? '');

            if (normalizedQuery && !term.includes(normalizedQuery)) {
                return false;
            }

            return !key || !completedIdeaKeys.has(key);
        });
    });
    const trackedIdeasWithPending = computed(() => {
        if (pagination.current_page !== 1) {
            return trackedIdeas.value;
        }

        return [
            ...dedupePendingIdeas([
                ...visiblePendingTrackedIdeas.value,
                ...persistedPendingTrackedIdeas.value,
            ]),
            ...readyTrackedIdeas.value,
        ];
    });

    const {
        execute: fetchTrackedIdeas,
        isReady: trackedIdeasAreReady,
        isLoading: trackedIdeasAreLoading,
    } = useAsyncState(fetchTrackedIdeasPromise, {}, { immediate: false });

    function fetchTrackedIdeasPromise(options = {}) {
        const pageNumber = normalizeInteger(options.page ?? pagination.current_page, 1);
        const perPage = normalizeInteger(options.limit ?? pagination.per_page, DEFAULT_LIMIT);
        const requestFilters = options.withFilters === false ? {} : buildFilterParams();
        const requestSort = normalizeSort(options.sort ?? serializeSort());
        const ready = normalizeReadyOption(options.ready ?? false);
        const params = {
            page: {
                number: pageNumber,
                limit: perPage,
            },
            ...(ready !== null ? { ready } : {}),
            ...(requestSort ? { sort: requestSort } : {}),
            ...(Object.keys(requestFilters).length ? { filter: requestFilters } : {}),
        };

        return preservedStore.get(['tracked-ideas', { params }]).then((data) => {
            trackedIdeas.value = sortToPreservedJson(data).map((record) => normalizeTrackedIdeaRecord(record));
            syncPagination(data?._jv?.json?.meta?.pagination, pageNumber, perPage);
            return trackedIdeas.value;
        });
    }

    function fetchPendingTrackedIdeas(options = {}) {
        const hasExplicitReportIds = Object.hasOwn(options, 'reportIds');
        const reportIds = hasExplicitReportIds ? normalizeReportIds(options.reportIds) : [];
        const params = reportIds.length ? { report_ids: reportIds } : {};

        if (hasExplicitReportIds && !reportIds.length) {
            return Promise.resolve(pendingTrackedIdeas.value);
        }

        return preservedStore.get(['tracked-ideas/pending', { params }]).then((data) => {
            const records = sortToPreservedJson(data).map((record) => normalizeTrackedIdeaRecord(record));

            if (hasExplicitReportIds) {
                syncPendingTrackedIdeas(records, reportIds, {
                    preserveRequestedOptimisticRows: true,
                });
            } else {
                mergeFetchedPendingTrackedIdeas(records);
            }

            return hasExplicitReportIds ? records : pendingTrackedIdeas.value;
        });
    }

    function fetchTrackedIdeasByReportIds(reportIds, options = {}) {
        const normalizedReportIds = normalizeReportIds(reportIds);

        if (!normalizedReportIds.length) {
            return Promise.resolve([]);
        }

        const reportingPeriod = normalizeStringOrNull(options.reportingPeriod ?? filters.interest_trend_window) || DEFAULT_REPORTING_PERIOD;
        const params = {
            page: {
                number: 1,
                limit: Math.min(Math.max(normalizedReportIds.length, 1), 500),
            },
            report_ids: normalizedReportIds,
            filter: {
                'interest_trend.window': reportingPeriod,
            },
        };

        return preservedStore.get(['tracked-ideas', { params }]).then((data) => sortToPreservedJson(data).map((record) => normalizeTrackedIdeaRecord(record)));
    }

    function fetchReportProducts(options = {}) {
        if (!options.force && reportProductsLoaded.value) {
            return Promise.resolve(reportProducts.value);
        }

        if (reportProductsRequest) {
            return reportProductsRequest;
        }

        reportProductsRequest = preservedStore.get('tracked-ideas/report-products').then((data) => {
            reportProducts.value = sortToPreservedJson(data).map((record) => normalizeReportProductRecord(record));
            reportProductsLoaded.value = true;

            return reportProducts.value;
        }).finally(() => {
            reportProductsRequest = null;
        });

        return reportProductsRequest;
    }

    function hasReportForProduct(productId) {
        const normalizedProductId = normalizeInteger(productId, null, { min: 0 });

        if (normalizedProductId === null) {
            return false;
        }

        return reportProducts.value.some((report) => {
            return normalizeInteger(report?.product_id, null, { min: 0 }) === normalizedProductId;
        });
    }

    function buildFilterParams() {
        const params = {};

        if (filters.query) {
            params.query = filters.query;
        }

        if (filters.category_slug) {
            params.category_slug = filters.category_slug;
        }

        if (filters.market_opportunity.length) {
            params.market_opportunity = filters.market_opportunity;
        }

        if (filters.demand_level.length) {
            params.demand_level = filters.demand_level;
        }

        if (filters.competition_level.length) {
            params.competition_level = filters.competition_level;
        }

        if (filters.interest_trend_window) {
            params['interest_trend.window'] = filters.interest_trend_window;
        }

        return params;
    }

    function syncPagination(metaPagination = {}, currentPage, perPage) {
        pagination.current_page = normalizeInteger(metaPagination?.current_page, currentPage);
        pagination.per_page = normalizeInteger(metaPagination?.per_page, perPage);
        pagination.total = normalizeInteger(metaPagination?.total, 0);
        pagination.total_pages = normalizeInteger(metaPagination?.total_pages, 1);
        pagination.count = normalizeInteger(metaPagination?.count, trackedIdeas.value.length);
    }

    function setPage(page) {
        pagination.current_page = normalizeInteger(page, 1);
        return syncRouteQuery({ replace: false });
    }

    function setSort(key) {
        const normalizedKey = String(key || '').trim();

        if (!normalizedKey) {
            return Promise.resolve();
        }

        if (sortKey.value === normalizedKey) {
            sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
        } else {
            sortKey.value = normalizedKey;
            sortOrder.value = 'asc';
        }

        pagination.current_page = 1;

        return syncRouteQuery();
    }

    function setFilter(key, value) {
        if (!Object.hasOwn(filters, key)) {
            return Promise.resolve();
        }

        filters[key] = normalizeFilterValue(key, value);
        pagination.current_page = 1;

        return syncRouteQuery();
    }

    function resetFilters() {
        Object.assign(filters, createDefaultFilters());
        sortKey.value = DEFAULT_SORT_KEY;
        sortOrder.value = DEFAULT_SORT_ORDER;
        pagination.current_page = 1;

        return syncRouteQuery();
    }

    function serializeSort() {
        const normalizedKey = normalizeString(sortKey.value);

        if (!normalizedKey) {
            return null;
        }

        return `${sortOrder.value === 'desc' ? '-' : ''}${normalizedKey}`;
    }

    function normalizeFilterValue(key, value) {
        if (Array.isArray(filters[key])) {
            return normalizeStringArray(value);
        }

        return normalizeString(value);
    }

    function normalizeSort(value) {
        return normalizeStringOrNull(value);
    }

    function normalizeReadyOption(value) {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }

        const normalized = String(value).trim().toLowerCase();

        if (['1', 'true', 'yes', 'on'].includes(normalized)) {
            return 'true';
        }

        if (['0', 'false', 'no', 'off'].includes(normalized)) {
            return 'false';
        }

        return null;
    }

    function buildRouteQuery() {
        const query = {};
        const serializedSort = serializeSort();
        const defaultSort = `${DEFAULT_SORT_ORDER === 'desc' ? '-' : ''}${DEFAULT_SORT_KEY}`;

        if (filters.query) {
            query.query = filters.query;
        }

        if (filters.category_slug) {
            query.category_slug = filters.category_slug;
        }

        if (filters.market_opportunity.length) {
            query.market_opportunity = [...filters.market_opportunity];
        }

        if (filters.demand_level.length) {
            query.demand_level = [...filters.demand_level];
        }

        if (filters.competition_level.length) {
            query.competition_level = [...filters.competition_level];
        }

        if (filters.interest_trend_window && filters.interest_trend_window !== DEFAULT_REPORTING_PERIOD) {
            query.interest_trend_window = filters.interest_trend_window;
        }

        if (pagination.current_page > 1) {
            query.page = String(pagination.current_page);
        }

        if (serializedSort && serializedSort !== defaultSort) {
            query.sort = serializedSort;
        }

        return query;
    }

    function applyRouteQuery(routeQuery = {}) {
        const defaultFilters = createDefaultFilters();
        const normalizedSort = normalizeSort(routeQuery.sort) || `${DEFAULT_SORT_ORDER === 'desc' ? '-' : ''}${DEFAULT_SORT_KEY}`;

        filters.query = normalizeFilterValue('query', routeQuery.query ?? routeQuery.keyword ?? defaultFilters.query);
        filters.category_slug = normalizeFilterValue('category_slug', routeQuery.category_slug ?? defaultFilters.category_slug);
        filters.market_opportunity = normalizeFilterValue('market_opportunity', routeQuery.market_opportunity ?? defaultFilters.market_opportunity);
        filters.demand_level = normalizeFilterValue('demand_level', routeQuery.demand_level ?? defaultFilters.demand_level);
        filters.competition_level = normalizeFilterValue('competition_level', routeQuery.competition_level ?? defaultFilters.competition_level);
        filters.interest_trend_window = normalizeFilterValue('interest_trend_window', routeQuery.interest_trend_window ?? defaultFilters.interest_trend_window) || DEFAULT_REPORTING_PERIOD;
        pagination.current_page = normalizeInteger(routeQuery.page, 1);
        sortOrder.value = normalizedSort.startsWith('-') ? 'desc' : 'asc';
        sortKey.value = normalizedSort.replace(/^-/, '') || DEFAULT_SORT_KEY;
    }

    function syncRouteQuery(options = {}) {
        const { replace = true } = options;

        return router[replace ? 'replace' : 'push']({
            name: TRACKED_PRODUCT_IDEAS_ROUTE,
            query: buildRouteQuery(),
        });
    }

    function fetchFromRoute(routeQuery = route.query) {
        stopPendingTrackedIdeasPolling();
        applyRouteQuery(routeQuery);
        return Promise.all([
            fetchTrackedIdeas(0, { page: pagination.current_page, limit: DEFAULT_LIMIT, sort: serializeSort() }),
            fetchPendingTrackedIdeas().catch(() => pendingTrackedIdeas.value),
        ]).then(() => trackedIdeasWithPending.value)
            .finally(() => {
                startPendingTrackedIdeasPolling();
            });
    }

    function startPendingTrackedIdeasPolling() {
        const pendingReportIds = getPendingTrackedIdeaReportIds(pendingTrackedIdeas.value);

        if (!pendingReportIds.length) {
            stopPendingTrackedIdeasPolling();
            return;
        }

        if (pendingTrackedIdeasPoller !== null) {
            clearInterval(pendingTrackedIdeasPoller);
        }

        const pollToken = ++pendingTrackedIdeasPollToken;
        pendingTrackedIdeasPoller = window.setInterval(() => {
            void pollPendingTrackedIdeas(pollToken);
        }, PENDING_TRACKED_IDEAS_POLL_INTERVAL_MS);
    }

    function stopPendingTrackedIdeasPolling() {
        if (pendingTrackedIdeasPoller !== null) {
            clearInterval(pendingTrackedIdeasPoller);
            pendingTrackedIdeasPoller = null;
        }

        pendingTrackedIdeasPollToken += 1;
    }

    function pollPendingTrackedIdeas(pollToken = pendingTrackedIdeasPollToken) {
        if (pollToken !== pendingTrackedIdeasPollToken || pendingTrackedIdeasPollInFlight) {
            return Promise.resolve([]);
        }

        const pendingReportIds = getPendingTrackedIdeaReportIds(pendingTrackedIdeas.value);

        if (!pendingReportIds.length) {
            stopPendingTrackedIdeasPolling();
            return Promise.resolve([]);
        }

        const previousTrackedIdeas = [...pendingTrackedIdeas.value];
        pendingTrackedIdeasPollInFlight = true;

        return fetchPendingTrackedIdeas({ reportIds: pendingReportIds })
            .then((records = []) => {
                if (pollToken !== pendingTrackedIdeasPollToken) {
                    return [];
                }

                const nextPendingReportIds = new Set(getPendingTrackedIdeaReportIds(records));
                const maybeResolvedReportIds = pendingReportIds.filter((reportId) => !nextPendingReportIds.has(reportId));

                if (!maybeResolvedReportIds.length) {
                    return [];
                }

                return fetchTrackedIdeasByReportIds(maybeResolvedReportIds, {
                    reportingPeriod: filters.interest_trend_window,
                }).then((records) => {
                    if (pollToken === pendingTrackedIdeasPollToken) {
                        const resolvedReportIds = normalizeReportIds(records.map((record) => getReportId(record)));

                        if (resolvedReportIds.length) {
                            syncResolvedTrackedIdeas(records, resolvedReportIds, previousTrackedIdeas);
                        }
                    }

                    return records;
                });
            })
            .catch(() => [])
            .finally(() => {
                pendingTrackedIdeasPollInFlight = false;

                if (pollToken === pendingTrackedIdeasPollToken && !getPendingTrackedIdeaReportIds(pendingTrackedIdeas.value).length) {
                    stopPendingTrackedIdeasPolling();
                }
            });
    }

    function syncPendingTrackedIdeas(records, requestedReportIds = [], options = {}) {
        const { preserveRequestedOptimisticRows = false } = options;
        const requestedIdSet = new Set(normalizeReportIds(requestedReportIds));
        const nextPendingIdeas = [];
        const nextPendingIdeaIds = new Set();

        records.forEach((record) => {
            const reportId = getReportId(record);

            if (!reportId || nextPendingIdeaIds.has(reportId)) {
                return;
            }

            nextPendingIdeas.push(record);
            nextPendingIdeaIds.add(reportId);
        });

        pendingTrackedIdeas.value.forEach((record) => {
            const reportId = getReportId(record);
            const shouldPreserveOptimisticRecord = preserveRequestedOptimisticRows
                && requestedIdSet.has(reportId)
                && record?.is_optimistic_pending_row === true;

            if (!reportId || nextPendingIdeaIds.has(reportId)) {
                return;
            }

            if (requestedIdSet.has(reportId) && !shouldPreserveOptimisticRecord) {
                return;
            }

            nextPendingIdeas.push(record);
            nextPendingIdeaIds.add(reportId);
        });

        pendingTrackedIdeas.value = nextPendingIdeas;
    }

    function mergeFetchedPendingTrackedIdeas(records = []) {
        const confirmedPendingIds = new Set(getPendingTrackedIdeaReportIds(records));
        const optimisticRecords = pendingTrackedIdeas.value.filter((record) => {
            const reportId = getReportId(record);

            return record?.is_optimistic_pending_row === true
                && reportId
                && !confirmedPendingIds.has(reportId);
        });

        pendingTrackedIdeas.value = dedupePendingIdeas([
            ...records,
            ...optimisticRecords,
        ]);
    }

    function syncResolvedTrackedIdeas(records, resolvedReportIds = [], previousTrackedIdeas = []) {
        const resolvedIdSet = new Set(normalizeReportIds(resolvedReportIds));
        const currentRecordsByReportId = new Map();
        const resolvedRecordsByReportId = new Map();
        const mergedRecords = [];
        const mergedRecordIds = new Set();

        pendingTrackedIdeas.value.forEach((record) => {
            const reportId = getReportId(record);

            if (reportId && !currentRecordsByReportId.has(reportId)) {
                currentRecordsByReportId.set(reportId, record);
            }
        });

        records.forEach((record) => {
            const reportId = getReportId(record);

            if (reportId && !resolvedRecordsByReportId.has(reportId)) {
                resolvedRecordsByReportId.set(reportId, record);
            }
        });

        previousTrackedIdeas.forEach((record) => {
            const reportId = getReportId(record);

            if (!reportId || mergedRecordIds.has(reportId)) {
                return;
            }

            if (resolvedIdSet.has(reportId)) {
                const resolvedRecord = resolvedRecordsByReportId.get(reportId);

                if (resolvedRecord) {
                    mergedRecords.push(resolvedRecord);
                    mergedRecordIds.add(reportId);
                }

                return;
            }

            const currentRecord = currentRecordsByReportId.get(reportId);

            if (currentRecord) {
                mergedRecords.push(currentRecord);
                mergedRecordIds.add(reportId);
                currentRecordsByReportId.delete(reportId);
            }
        });

        currentRecordsByReportId.forEach((record, reportId) => {
            if (!mergedRecordIds.has(reportId)) {
                mergedRecords.push(record);
                mergedRecordIds.add(reportId);
            }
        });

        resolvedRecordsByReportId.forEach((record, reportId) => {
            if (!mergedRecordIds.has(reportId)) {
                mergedRecords.push(record);
                mergedRecordIds.add(reportId);
            }
        });

        pendingTrackedIdeas.value = dedupePendingIdeas(mergedRecords);
    }

    function seedPendingTrackedIdea(record = {}) {
        const reportId = getReportId(record);
        const term = normalizeString(record?.term ?? record?.title ?? record?.name);
        const status = normalizeLowercaseString(record?.status);

        if (!reportId || !term || (status && !isPendingStatus(status))) {
            return null;
        }

        const optimisticRecord = {
            ...record,
            id: reportId,
            report_id: reportId,
            term,
            title: term,
            status: status || 'pending',
            is_pending_row: true,
            is_optimistic_pending_row: true,
        };

        pendingTrackedIdeas.value = dedupePendingIdeas([
            optimisticRecord,
            ...pendingTrackedIdeas.value,
        ]);

        return optimisticRecord;
    }

    return {
        filters,
        pagination,
        totalPages,
        sortKey,
        sortOrder,
        trackedIdeas,
        reportProducts,
        reportProductsLoaded,
        pendingTrackedIdeas,
        visiblePendingTrackedIdeas,
        trackedIdeasWithPending,
        buildRouteQuery,
        applyRouteQuery,
        fetchFromRoute,
        fetchTrackedIdeas,
        fetchTrackedIdeasByReportIds,
        fetchPendingTrackedIdeas,
        fetchReportProducts,
        hasReportForProduct,
        seedPendingTrackedIdea,
        setPage,
        setSort,
        setFilter,
        resetFilters,
        startPendingTrackedIdeasPolling,
        stopPendingTrackedIdeasPolling,
        trackedIdeasAreReady,
        trackedIdeasAreLoading,
    };
});

function normalizeReportProductRecord(record) {
    if (!record || typeof record !== 'object') {
        return record;
    }

    return {
        ...record,
        id: normalizeReportId(record.report_id ?? record.id) ?? record.id,
        report_id: normalizeReportId(record.report_id ?? record.id),
        product_id: normalizeInteger(record.product_id, null, { min: 0 }),
    };
}

function normalizeReportIds(value) {
    return normalizeStringArray(value).map((reportId) => normalizeReportId(reportId)).filter(Boolean);
}

function normalizeReportId(value) {
    return normalizeStringOrNull(value);
}

function getReportId(record) {
    return normalizeReportId(record?.report_id ?? record?._jv?.id ?? record?.id);
}

function getPendingTrackedIdeaReportIds(records = []) {
    return records
        .filter((record) => record?.is_pending_row === true)
        .map((record) => getReportId(record))
        .filter(Boolean);
}

function buildIdeaMatchKey(record) {
    const productId = normalizeInteger(record?.product_id ?? record?.source_id ?? record?.id, null, { min: 0 });

    if (productId !== null) {
        return `product:${productId}`;
    }

    const term = normalizeLowercaseString(record?.term ?? record?.title ?? '');

    return term ? `term:${term}` : null;
}

function normalizeTrackedIdeaRecord(record) {
    if (!record || typeof record !== 'object') {
        return record;
    }

    const isPending = isPendingTrackedIdeaRecord(record);

    if (isPending || record.is_pending_row === true) {
        return {
            ...record,
            is_pending_row: isPending,
        };
    }

    return record;
}

function isPendingTrackedIdeaRecord(record) {
    if (!record || typeof record !== 'object') {
        return false;
    }

    const status = normalizeLowercaseString(record.status);

    if (isPendingStatus(status)) {
        return true;
    }

    if (record.is_pending_row === true && !status) {
        return true;
    }

    return record.is_ready_for_reports === false && status !== 'ready' && status !== 'insufficient_data';
}

function isPendingStatus(status) {
    return ['pending', 'in_progress', 'in-progress'].includes(status);
}


function dedupePendingIdeas(records) {
    const pendingIdeas = [];
    const pendingIdeaIds = new Set();

    records.forEach((record) => {
        const reportId = getReportId(record);

        if (!reportId || pendingIdeaIds.has(reportId)) {
            return;
        }

        pendingIdeas.push(record);
        pendingIdeaIds.add(reportId);
    });

    return pendingIdeas;
}

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTrackedProductIdeasStore, import.meta.hot));
}
